/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import React, { type RefObject } from "react";
import { ChatCircle } from "@/components/ui/icons";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { Button } from "@/components/ui/button";
import { MessageListSkeleton } from "@/components/messages/MessageListSkeleton";
import { shouldShowEmptyMessageState } from "@/lib/message-feed-state";
import { JUMP_TRACE, MULTIPLEX_DEBUG } from "@/lib/debug-flags";
import { csMark, csNow, csLog } from "@/lib/channel-switch-profile";
import { scrollLog, scrollNow, scrollLogDur } from "@/lib/scroll-profile";

function jumpLog(label: string, detail?: Record<string, unknown>) {
  if (!JUMP_TRACE) return;
  const tail = detail
    ? " " +
      Object.entries(detail)
        .map(
          ([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`,
        )
        .join(" ")
    : "";
  console.debug(`[jump] ${label}${tail}`);
}
import type { ScrollSnapshot } from "@/hooks/use-scroll-anchor";
import type { useMessageWindow } from "@/hooks/use-message-window";
import { cn } from "@/lib/utils";

// ─── Debug logging ───────────────────────────────────────────────────

function scrollDebug(label: string, detail?: Record<string, unknown>) {
  if (
    typeof window !== "undefined" &&
    (window as { __ANO_SCROLL_DEBUG__?: boolean }).__ANO_SCROLL_DEBUG__
  ) {
    console.debug(`[scroll] ${label}`, detail);
  }
}

// ─── Types ───────────────────────────────────────────────────────────

export type RenderedRow = {
  key: string;
  messageId: string | null;
  node: React.ReactNode;
};

export type ChannelMessagesStatus = {
  type: "unknown" | "complete" | "error";
  retry?: () => void;
};

type MessageWindowHook = ReturnType<typeof useMessageWindow>;

export type MessageScrollContainerProps = {
  channelId: string;
  rows: RenderedRow[];
  scrollRef: RefObject<HTMLDivElement | null>;
  topSentinelRef: RefObject<HTMLDivElement | null>;
  bottomSentinelRef: RefObject<HTMLDivElement | null>;

  // Scroll anchor (capture/restore functions from useScrollAnchor)
  captureSnapshot: () => ScrollSnapshot | null;
  restoreSnapshot: (snapshot?: ScrollSnapshot | null) => void;
  lastSnapshotRef: RefObject<ScrollSnapshot | null>;

  isNearBottomRef: RefObject<boolean>;
  currentAnchorIdRef: RefObject<string | null>;
  scrollAnchorByChannel: RefObject<
    Record<string, { id: string; neighborId: string | null; offset: number }>
  >;
  messageIndexById: Map<string, number>;

  // Message window
  messageWindow: MessageWindowHook;
  windowBoundsKey: string;
  /**
   * True when MessageListView has a pending scrollTarget (resolving or
   * found-but-not-yet-scrolled). While pending, we skip the
   * windowBoundsKey-changed branch in componentDidUpdate — the
   * scrollTarget layout effect will set scrollTop. Without this gate,
   * the snapshot's `restore-scrollBottom` fallback (when the previous
   * topmost-visible message isn't in the new slice) pins to the
   * bottom of the new slice for one frame before the scrollTarget
   * effect lands the actual anchor — visible as a "jump down" on
   * mid-list channel-switch-back.
   */
  scrollTargetPending: boolean;

  // Status
  isLoadingOlder: boolean;
  hasMoreMessages: boolean;
  channelMessagesStatus: ChannelMessagesStatus;
  showMessageLoadingSkeleton: boolean;
  isUsingReconnectSnapshot: boolean;
  messageListLength: number;

  // Callbacks
  onNearBottomChange: (nearBottom: boolean) => void;
};

// ─── Snapshot type for getSnapshotBeforeUpdate ───────────────────────

type Snapshot = {
  scrollBottom: number;
  anchorMessageId: string | null;
  anchorOffset: number;
  wasNearBottom: boolean;
} | null;

// Two thresholds create hysteresis. `STAY_AT_BOTTOM_THRESHOLD` is how
// far the user can scroll up before they're no longer considered to
// be "following the live tail" — i.e. how aggressive the
// follow-bottom behavior is. The previous 600px (≈ full viewport)
// kept the user pinned even after a deliberate scroll-up of several
// rows: when new data arrived (live message OR Zero limit bump
// growing `messages.length`), Phase 1 reconciliation pin-bottomed
// them back, producing a visible teleport away from the rows they
// were starting to read. 150px (≈ 2-3 rows) matches the Discord /
// Slack convention: any meaningful scroll-up cancels the follow and
// hands re-attach off to the `JumpToBottomButton`.
const STAY_AT_BOTTOM_THRESHOLD = 150;
const RETURN_TO_BOTTOM_THRESHOLD = 30;

/**
 * Class component for the message scroll container.
 *
 * Uses getSnapshotBeforeUpdate → componentDidUpdate for synchronous
 * scroll anchor capture/restore — the same pattern Signal Desktop uses.
 * This eliminates the timing gap that functional components have between
 * useLayoutEffect and the DOM commit.
 */
export class MessageScrollContainer extends React.Component<MessageScrollContainerProps> {
  // Only re-render when rendering-relevant props change.
  //
  // Invariant: non-rendering props (captureSnapshot, restoreSnapshot, ref
  // objects, messageIndexById, scrollAnchorByChannel, messageWindow) are either
  // stable refs or only read in event handlers / lifecycle methods. Their
  // identity changes don't affect render output.
  //
  // messageWindow IS read in render() for the "beginning of conversation"
  // banner (windowStartIndex === 0), but that condition is covered by the
  // windowBoundsKey comparison below.
  shouldComponentUpdate(nextProps: MessageScrollContainerProps) {
    return (
      nextProps.rows !== this.props.rows ||
      nextProps.windowBoundsKey !== this.props.windowBoundsKey ||
      nextProps.channelId !== this.props.channelId ||
      nextProps.isLoadingOlder !== this.props.isLoadingOlder ||
      nextProps.hasMoreMessages !== this.props.hasMoreMessages ||
      nextProps.isUsingReconnectSnapshot !==
        this.props.isUsingReconnectSnapshot ||
      nextProps.showMessageLoadingSkeleton !==
        this.props.showMessageLoadingSkeleton ||
      nextProps.messageListLength !== this.props.messageListLength ||
      nextProps.channelMessagesStatus.type !==
        this.props.channelMessagesStatus.type ||
      nextProps.scrollTargetPending !== this.props.scrollTargetPending
    );
  }

  private contentRef = React.createRef<HTMLDivElement>();
  private scrollRafId: number | null = null;
  private rePinRafId: number | null = null;
  private userScrollingUntil = 0;
  private channelSwitching = false;
  private channelSwitchTimer: number | null = null;
  private scrollIdleTimer: number | null = null;
  private suppressPointer = false;
  private resizeObserver: ResizeObserver | null = null;
  /**
   * scrollTop captured at moments we know the user is at the live
   * bottom — initial pin, channel-switch pin, wbk-pin, IO flip into
   * nearBottom=true, and on every scroll-handler tick while
   * `isNearBottomRef.current` is true.
   *
   * Used to distinguish two superficially-identical "no longer near
   * bottom" causes:
   *   (a) user scrolled up — scrollTop CHANGED → flip flag, normal
   *       behavior.
   *   (b) content grew below the viewport (GIF/embed/image finished
   *       loading, or async data arrived) — scrollTop UNCHANGED → the
   *       sentinel exited the rootMargin band only because the content
   *       was added below the user's anchor. Re-pin to new bottom and
   *       DON'T flip the flag.
   *
   * Without this guard, after a GIF loads post-mount the threshold
   * math (non-spike) OR the bottom-sentinel IO (spike) flips
   * `isNearBottomRef=false`, then ResizeObserver fires next, sees the
   * false flag, and runs `restoreSnapshot()` — which preserves the
   * captured anchor's offset (anchor hasn't moved, so scrollTop stays
   * at the pre-grow bottom). Result: user sees half the GIF, with the
   * rest cut off below the viewport.
   */
  private scrollTopWhileNearBottom = 0;

  /**
   * scrollHeight captured at the same moments as
   * `scrollTopWhileNearBottom`. Pair-tracked so the content-grew
   * re-pin in the scroll handler can distinguish two superficially
   * identical "scrollTop ≈ baseline AND no longer near-bottom" cases:
   *
   *   (a) Content actually grew below viewport (GIF loaded, etc.)
   *       — scrollHeight > scrollHeightWhileNearBottom. Re-pin is
   *       correct: push user down to keep the new content visible.
   *
   *   (b) User crossed the near-bottom threshold by 1-2 px (e.g.
   *       slow scroll-up at the boundary). scrollHeight is
   *       unchanged. Re-pinning here yanks them back to bottom —
   *       the rubber-band the user feels on slow scroll-up.
   *
   * Without the scrollHeight guard, case (b) gets misdiagnosed as
   * (a) because scrollTop barely moved between the last near-bottom
   * tick and the first non-near-bottom tick.
   */
  private scrollHeightWhileNearBottom = 0;

  /**
   * Pin scrollTop to bottom now, then again in the next animation frame.
   *
   * Why two passes: row descendants hydrate via `usePostMountFrame` on
   * rAF+1 (ThreadReplyPreview, MessageActions). On frame 0 the synchronous
   * pin uses a scrollHeight that's missing those descendants' height —
   * scrollTop lands above the true bottom and the user sees the bottom
   * rows snap in when ResizeObserver finally catches up. The rAF re-pin
   * runs *after* hydration commits but before the next paint, so the
   * user only ever sees the final correctly-pinned state.
   */
  private pinToBottomTwoPass(reason: string) {
    const pin = (pass: "sync" | "rAF") => {
      const el = this.props.scrollRef.current;
      if (!el) return;
      // `mount` and `channel-switch` always pin: at those moments the
      // user can't have scrolled meaningfully — the rAF re-pin exists
      // purely to compensate for descendant hydration (thread previews,
      // reactions etc. via `usePostMountFrame` on rAF+1). Gating the
      // re-pin on `isNearBottomRef.current` here was fine when
      // `STAY_AT_BOTTOM_THRESHOLD` was 600px (≈ full viewport) — the
      // flag never flipped false during the rAF gap. With the smaller
      // 150px threshold, hydration height alone can exceed the
      // threshold, the flag flips false, and the re-pin skips —
      // visible as ~1-2 row heights hidden below the viewport on
      // first paint.
      if (
        !this.props.isNearBottomRef.current &&
        reason !== "channel-switch" &&
        reason !== "mount"
      ) {
        scrollLog("pin-skip", {
          reason,
          pass,
          near: this.props.isNearBottomRef.current,
        });
        return;
      }
      const target = el.scrollHeight - el.clientHeight;
      const before = el.scrollTop;
      el.scrollTop = target;
      scrollLog("pin", {
        reason,
        pass,
        before: before.toFixed(0),
        target: target.toFixed(0),
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      });
    };
    pin("sync");
    if (this.rePinRafId !== null) cancelAnimationFrame(this.rePinRafId);
    this.rePinRafId = requestAnimationFrame(() => {
      this.rePinRafId = null;
      pin("rAF");
    });
  }

  componentDidMount() {
    this.setupResizeObserver();
    // Warn at mount if CSS overflow-anchor is anything but "auto":
    // both the shift-up-keeps-anchor-stable and the "browser preserved
    // scrollBottom" paths in this component depend on the browser
    // default. If something in the scroll subtree overrides it the
    // chain silently breaks, so log a one-time diagnostic.
    this.verifyOverflowAnchor();
    // Initial pin to bottom (two-pass — see pinToBottomTwoPass)
    this.pinToBottomTwoPass("mount");
    // Baseline for content-growth detection: after pin, scrollTop is
    // at the bottom of the current scrollHeight. If a later observer
    // tick reads scrollTop unchanged AND scrollHeight grown, it's
    // async media load, not user scroll — re-pin instead of flipping.
    this.scrollTopWhileNearBottom =
      this.props.scrollRef.current?.scrollTop ?? 0;
    this.scrollHeightWhileNearBottom =
      this.props.scrollRef.current?.scrollHeight ?? 0;
    if (MULTIPLEX_DEBUG) {
      const el = this.props.scrollRef.current;
      console.debug(
        `[mx-scroll] MOUNT ch=${this.props.channelId?.slice(-6) ?? "-"} scrollTop=${el?.scrollTop} scrollHeight=${el?.scrollHeight}`,
      );
    }
  }

  componentWillUnmount() {
    if (this.scrollRafId !== null) cancelAnimationFrame(this.scrollRafId);
    if (this.rePinRafId !== null) cancelAnimationFrame(this.rePinRafId);
    if (this.channelSwitchTimer !== null) clearTimeout(this.channelSwitchTimer);
    if (this.scrollIdleTimer !== null) clearTimeout(this.scrollIdleTimer);
    this.resizeObserver?.disconnect();
    // Reset pointer-events in case scroll suppression was active
    const content = this.contentRef.current;
    if (content) content.style.pointerEvents = "";
    if (MULTIPLEX_DEBUG) {
      const el = this.props.scrollRef.current;
      console.debug(
        `[mx-scroll] UNMOUNT ch=${this.props.channelId?.slice(-6) ?? "-"} scrollTop=${el?.scrollTop}`,
      );
    }
  }

  // ─── Signal's core pattern: synchronous snapshot before DOM commit ──

  getSnapshotBeforeUpdate(prevProps: MessageScrollContainerProps): Snapshot {
    // Channel switch — pin to bottom, no snapshot needed
    if (prevProps.channelId !== this.props.channelId) {
      this.channelSwitching = true;
      if (this.channelSwitchTimer !== null)
        clearTimeout(this.channelSwitchTimer);
      this.channelSwitchTimer = window.setTimeout(() => {
        this.channelSwitching = false;
      }, 300);
      scrollDebug("getSnapshot channel-switch", {
        channelId: this.props.channelId?.slice(-6),
      });
      return null;
    }

    // Window bounds changed — capture scroll position NOW, before React
    // commits the new DOM. This is the key advantage over useLayoutEffect.
    if (prevProps.windowBoundsKey !== this.props.windowBoundsKey) {
      const el = this.props.scrollRef.current;
      if (!el) return null;

      const containerTop = el.getBoundingClientRect().top;
      const messageNodes =
        el.querySelectorAll<HTMLElement>("[data-message-id]");

      let anchorMessageId: string | null = null;
      let anchorOffset = 0;

      for (const node of messageNodes) {
        if (node.getBoundingClientRect().bottom > containerTop + 1) {
          anchorMessageId = node.dataset.messageId ?? null;
          anchorOffset = node.getBoundingClientRect().top - containerTop;
          break;
        }
      }

      const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

      scrollDebug("getSnapshot", {
        windowBoundsKey: this.props.windowBoundsKey,
        anchorId: anchorMessageId?.slice(-6),
        scrollBottom,
      });

      jumpLog("snapshot capture", {
        windowBoundsKey: `${prevProps.windowBoundsKey}→${this.props.windowBoundsKey}`,
        anchorId: anchorMessageId?.slice(-6) ?? null,
        anchorOffset,
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        scrollBottom,
        wasNearBottom: this.props.isNearBottomRef.current,
      });
      return {
        scrollBottom,
        anchorMessageId,
        anchorOffset,
        wasNearBottom: this.props.isNearBottomRef.current,
      };
    }

    return null;
  }

  componentDidUpdate(
    prevProps: MessageScrollContainerProps,
    _prevState: unknown,
    snapshot: Snapshot,
  ) {
    const cduT0 = csNow();
    const scT0 = scrollNow();
    const wbkChanged = prevProps.windowBoundsKey !== this.props.windowBoundsKey;
    try {
      this.componentDidUpdateImpl(prevProps, snapshot);
    } finally {
      // Total cdu duration — includes channel-switch pin-to-bottom,
      // window-bounds-key snapshot restore, scrollTarget application,
      // anchor save, and the DOM measurements inside each. If high,
      // the post-commit scroll work is a longtask contributor.
      csLog(
        "MessageScrollContainer.cdu",
        cduT0,
        `ch=${this.props.channelId?.slice(-6) ?? "-"}`,
      );
      // Mirror for ano-scroll-profile so scrolling sessions (where the
      // cs profile is rarely on) still get commit-duration attribution.
      // Only logged on bounds-key change — the common case for a
      // mid-scroll commit. Other cdu callers (channel switch, etc.)
      // already log via [cs].
      if (wbkChanged) {
        scrollLogDur("wbk-commit", scT0, {
          bounds: `${prevProps.windowBoundsKey}→${this.props.windowBoundsKey}`,
          rows: this.props.rows.length,
          ch: this.props.channelId?.slice(-6) ?? "-",
        });
      }
    }
  }

  private componentDidUpdateImpl(
    prevProps: MessageScrollContainerProps,
    snapshot: Snapshot,
  ) {
    const el = this.props.scrollRef.current;
    if (!el) return;

    // Channel switch — clear stale snapshot, then pin-to-bottom UNLESS
    // there's a saved anchor for this channel (MessageListView will
    // restore via scrollTarget). Pinning a saved-anchor channel to
    // bottom kicks off a chain of false `isNearBottomRef = true`
    // → snapshot.wasNearBottom = true on the focus-target rAF
    // expansion → snap-to-conversation-bottom and the user sees the
    // viewport "jump down" on switch-back to a mid-list anchor.
    if (prevProps.channelId !== this.props.channelId) {
      // Cancel any pin-to-bottom rAF still pending from the PREVIOUS channel.
      // The single persistent fiber (MULTIPLEX_CAP=1) carries `rePinRafId`
      // across switches; its callback `pin("rAF")` unconditionally pins to
      // bottom for channel-switch/mount. On a rapid A→B→C switch where C has
      // a saved anchor, that stale rAF would fire AFTER the saved-anchor
      // restore below returns and teleport C to its live tail, destroying the
      // restored position. The pin-bottom branch reschedules its own rAF via
      // pinToBottomTwoPass, so clearing here is safe for both paths.
      if (this.rePinRafId !== null) {
        cancelAnimationFrame(this.rePinRafId);
        this.rePinRafId = null;
      }
      this.props.lastSnapshotRef.current = null;
      const channelId = this.props.channelId;
      const savedAnchor = channelId
        ? this.props.scrollAnchorByChannel.current[channelId]
        : null;

      if (savedAnchor) {
        // Warm switch — useMessageWindow seeded the initial slice
        // around this anchor (initialAnchor option), so the element
        // is already in the new DOM. Apply the saved scrollTop
        // delta synchronously here, before the browser paints, so
        // the first frame lands at the saved position — no
        // intermediate "newest messages" flash.
        //
        // If the anchor element isn't in the DOM (cold channel —
        // useMessageWindow fell back to pin-bottom because the id
        // wasn't in `messages` yet), skip the apply and let
        // MessageListView's scrollTarget useLayoutEffect handle it
        // once the id resolves.
        const anchorEl = el.querySelector<HTMLElement>(
          `[data-message-id="${CSS.escape(savedAnchor.id)}"]`,
        );
        if (anchorEl) {
          const containerTop = el.getBoundingClientRect().top;
          const currentOffset =
            anchorEl.getBoundingClientRect().top - containerTop;
          const delta = currentOffset - savedAnchor.offset;
          el.scrollTop += delta;
          csMark("settled", channelId);
          jumpLog("cdu channel-switch restore-anchor", {
            ch: channelId?.slice(-6),
            anchorId: savedAnchor.id.slice(-6),
            delta,
            scrollTop: el.scrollTop,
            scrollHeight: el.scrollHeight,
          });
          scrollDebug("componentDidUpdate channel-switch restore-anchor", {
            anchorId: savedAnchor.id.slice(-6),
            delta,
            scrollTop: el.scrollTop,
          });
          return;
        }
        // Anchor not in DOM yet — let scrollTarget resolve it.
        jumpLog("cdu channel-switch anchor-pending", {
          ch: channelId?.slice(-6),
          anchorId: savedAnchor.id.slice(-6),
          scrollTop: el.scrollTop,
        });
        scrollDebug("componentDidUpdate channel-switch anchor-pending", {
          anchorId: savedAnchor.id.slice(-6),
          scrollTop: el.scrollTop,
        });
        return;
      }

      // Cold/near-bottom switch — no saved anchor, pin to bottom.
      this.pinToBottomTwoPass("channel-switch");
      this.scrollTopWhileNearBottom = el.scrollTop;
      this.scrollHeightWhileNearBottom = el.scrollHeight;
      csMark("settled", channelId);
      jumpLog("cdu channel-switch pin-bottom", {
        ch: channelId?.slice(-6),
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        isNearBottom: this.props.isNearBottomRef.current,
      });
      scrollDebug("componentDidUpdate channel-switch pin-bottom", {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
      });
      return;
    }

    // Window bounds changed — restore from snapshot.
    //
    // Skip restore entirely while a scrollTarget is pending: that's an
    // intentional navigation (channel-switch-back to a saved anchor,
    // notification jump, in-app jump) and the scrollTarget layout
    // effect will set scrollTop correctly. Running the snapshot
    // restore here would either pin-to-bottom or scrollBottom-fallback
    // before the scrollTarget effect can apply, producing a
    // visible-for-one-frame jump.
    if (
      snapshot &&
      prevProps.windowBoundsKey !== this.props.windowBoundsKey &&
      !this.props.scrollTargetPending
    ) {
      // Detect "no new tail row arrived": the rendered slice's last
      // message id is unchanged. Covers two distinct cases that look
      // the same to the bounds-key check:
      //
      //   (1) Shift-up dispatch — window grew leftward, lastId stable.
      //   (2) Cold-hydration prepend — Zero IVM delivered older history
      //       in the same commit as a user wheel-up. Bounds-key may
      //       teleport in index space (270:300 → 529:599) because the
      //       prepend shifted indices by +299, but the user's visible
      //       last row is the same message.
      //
      // In both cases the user's intent / view position must be
      // preserved — pin-to-bottom would yank them to the absolute end
      // of the new slice (rubber-band-to-bottom on wheel-up). Falling
      // through to the anchor-restore branch keeps overflow-anchor's
      // natural preservation in effect.
      //
      // The genuine pin-to-bottom case (live message at tail while
      // user is near-bottom) DOES change lastId — handled normally.
      // Walk both row lists from the end to find the most recent
      // messageId (rows can include trailing non-message entries like
      // typing indicators / date separators with messageId=null).
      const lastMessageIdOf = (rows: RenderedRow[]): string | null => {
        for (let i = rows.length - 1; i >= 0; i--) {
          if (rows[i].messageId) return rows[i].messageId;
        }
        return null;
      };
      const prevLastMessageId = lastMessageIdOf(prevProps.rows);
      const newLastMessageId = lastMessageIdOf(this.props.rows);
      const tailUnchanged =
        prevLastMessageId !== null &&
        newLastMessageId !== null &&
        prevLastMessageId === newLastMessageId;

      // `isNearBottomRef` only updates on scroll events — if scrollTop
      // hasn't moved since the last update, the ref is stale. The
      // canonical case: channel-switch pin sets scrollTop to bottom +
      // ref=true, then row descendants hydrate (rAF+1) and grow
      // scrollHeight BELOW the viewport. No scroll event fires (scrollTop
      // unchanged), so the ref stays at true — but the user is no longer
      // mechanically near the bottom. snapshot.scrollBottom reads the
      // truth: if it's past the same threshold the scroll handler uses
      // (STAY_AT_BOTTOM_THRESHOLD = 150), the ref is stale and we should
      // re-pin to close the gap (the "half-GIF on channel open" case).
      //
      // Mid-scroll wheel-up at the live tail has snapshot.scrollBottom in
      // the single-digit px range (user moved 1-2px), so it stays in the
      // preserve path — keeping the rubber-band fix from earlier.
      const staleNearBottomRef =
        snapshot.wasNearBottom &&
        snapshot.scrollBottom > STAY_AT_BOTTOM_THRESHOLD;

      if (snapshot.wasNearBottom && tailUnchanged && !staleNearBottomRef) {
        scrollLog("wbk skip pin-bottom (tail unchanged)", {
          bounds: this.props.windowBoundsKey,
          lastMsg: newLastMessageId?.slice(-6),
          scrollBottom: snapshot.scrollBottom.toFixed(0),
        });
      }

      if (snapshot.wasNearBottom && (!tailUnchanged || staleNearBottomRef)) {
        if (staleNearBottomRef && tailUnchanged) {
          scrollLog("wbk pin-bottom (stale-ref close gap)", {
            bounds: this.props.windowBoundsKey,
            scrollBottom: snapshot.scrollBottom.toFixed(0),
          });
        }
        const before = el.scrollTop;
        // Two-pass to absorb post-mount-frame hydration (see pinToBottomTwoPass)
        this.pinToBottomTwoPass("window-bounds");
        this.scrollTopWhileNearBottom = el.scrollTop;
        this.scrollHeightWhileNearBottom = el.scrollHeight;
        scrollLog("wbk pin-bottom", {
          bounds: this.props.windowBoundsKey,
          scrollTop: `${before.toFixed(0)}→${el.scrollTop.toFixed(0)}`,
        });
        jumpLog("cdu wbk pin-bottom", {
          windowBoundsKey: this.props.windowBoundsKey,
          scrollTopBefore: before,
          scrollTopAfter: el.scrollTop,
          scrollHeight: el.scrollHeight,
        });
        scrollDebug("componentDidUpdate pin-bottom", {
          windowBoundsKey: this.props.windowBoundsKey,
          scrollTop: el.scrollTop,
        });
      } else if (snapshot.anchorMessageId) {
        // Find the anchor element in the NEW DOM and restore offset
        const anchorEl = el.querySelector<HTMLElement>(
          `[data-message-id="${CSS.escape(snapshot.anchorMessageId)}"]`,
        );
        if (anchorEl) {
          const containerTop = el.getBoundingClientRect().top;
          const currentOffset =
            anchorEl.getBoundingClientRect().top - containerTop;
          const delta = currentOffset - snapshot.anchorOffset;
          const before = el.scrollTop;
          el.scrollTop += delta;
          scrollLog("wbk restore-anchor", {
            anchor: snapshot.anchorMessageId.slice(-6),
            delta: delta.toFixed(0),
            scrollTop: `${before.toFixed(0)}→${el.scrollTop.toFixed(0)}`,
          });
          jumpLog("cdu wbk restore-anchor", {
            anchorId: snapshot.anchorMessageId.slice(-6),
            delta,
            scrollTopBefore: before,
            scrollTopAfter: el.scrollTop,
            scrollHeight: el.scrollHeight,
          });
          scrollDebug("componentDidUpdate restore anchor", {
            anchorId: snapshot.anchorMessageId.slice(-6),
            delta,
            scrollTop: el.scrollTop,
          });
        } else {
          // Anchor not in the new DOM — the slice teleported away from
          // the user's previous viewport (e.g. focus-target rAF
          // expansion racing with a Phase 1 prepend-reconciliation
          // recentering on a different anchorSnapshotRef). The
          // previous "scrollBottom math" fallback would pin to the
          // bottom of the new slice, which produces a visible jump
          // since the new slice's bottom is unrelated to where the
          // user actually wants to be. Doing nothing is strictly
          // better: scrollTarget effect (if active) places scrollTop
          // correctly, the browser auto-clamps if needed, and the
          // next render-or-scroll cycle settles things. Snapshot
          // logged for diagnostics only.
          scrollLog("wbk anchor-lost noop", {
            scrollBottom: snapshot.scrollBottom.toFixed(0),
            scrollTop: el.scrollTop.toFixed(0),
          });
          jumpLog("cdu wbk anchor-lost noop", {
            scrollBottom: snapshot.scrollBottom,
            scrollTop: el.scrollTop,
            scrollHeight: el.scrollHeight,
          });
          scrollDebug("componentDidUpdate anchor-lost noop", {
            scrollBottom: snapshot.scrollBottom,
            scrollTop: el.scrollTop,
          });
        }
      }
    }

    // Re-setup ResizeObserver if contentRef was null at mount (e.g., empty state
    // rendered first, then messages arrived)
    if (!this.resizeObserver && this.contentRef.current) {
      this.setupResizeObserver();
    }
  }

  // ─── ResizeObserver ────────────────────────────────────────────────

  private setupResizeObserver() {
    const content = this.contentRef.current;
    const scroll = this.props.scrollRef.current;
    if (!content || !scroll) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      // During the 300ms after a channel switch, the restore-snapshot
      // branch would use a stale snapshot — keep suppressing that.
      // BUT allow the pin-to-bottom branch through: after the channel-
      // switch pin sets isNearBottomRef=true, descendants hydrate
      // (rAF+1, then media loads), growing scrollHeight BELOW the
      // viewport. Without an RO re-pin we get the visible "half-GIF
      // then snap" — channel-switch pin lands at the bottom of the
      // unhydrated content, GIF/embed grows below, user sees the top
      // of it, and only the eventual cold-hydration emit (sometimes
      // seconds later) triggers our wbk stale-ref pin to close the
      // gap. Allowing RO pin here closes it as soon as scrollHeight
      // grows, within one frame.
      if (this.channelSwitching && !this.props.isNearBottomRef.current) {
        return;
      }

      // Ignore resize events where the observed element has zero size —
      // those fire when an ancestor goes `display: none` (e.g. an inactive
      // MessageArea instance in the multiplex layer). Acting on them would
      // read corrupt scroll metrics (scrollTop/scrollHeight clamp to 0) and
      // either flip isNearBottom to true or restore to a wrong offset. The
      // counterpart show-transition event has real dimensions and is the
      // one we actually want to act on.
      const entry = entries[0];
      if (
        entry &&
        entry.contentRect.width === 0 &&
        entry.contentRect.height === 0
      ) {
        if (MULTIPLEX_DEBUG)
          console.debug(
            `[mx-scroll] RO skip-zero ch=${this.props.channelId?.slice(-6) ?? "-"}`,
          );
        return;
      }

      if (this.props.isNearBottomRef.current) {
        const before = scroll.scrollTop;
        const target = scroll.scrollHeight - scroll.clientHeight;
        scroll.scrollTop = target;
        this.scrollTopWhileNearBottom = scroll.scrollTop;
        this.scrollHeightWhileNearBottom = scroll.scrollHeight;
        scrollLog("RO pin", {
          before: before.toFixed(0),
          target: target.toFixed(0),
          scrollHeight: scroll.scrollHeight,
          clientHeight: scroll.clientHeight,
        });
        if (MULTIPLEX_DEBUG)
          console.debug(
            `[mx-scroll] RO pin-bottom ch=${this.props.channelId?.slice(-6) ?? "-"} scrollTop-pre=${before} scrollHeight=${scroll.scrollHeight}`,
          );
      } else if (Date.now() < this.userScrollingUntil) {
        // Suppress during active scroll
        scrollLog("RO suppressed user-scrolling", {
          scrollTop: scroll.scrollTop.toFixed(0),
        });
      } else {
        // Safety net for the disjoint-bounds shift-down case — when the
        // captured anchor isn't in the new DOM, fall back to scrollBottom-
        // math (`scrollTop = scrollHeight - clientHeight - savedBottom`)
        // which preserves the user's "distance from live tail" even when
        // CSS overflow-anchor has no surviving anchor to anchor to.
        // Runs post-layout (RO fires on rAF+1 after hydration settles),
        // so the hydration race that motivated dropping the synchronous
        // cdu-wbk restore doesn't apply here.
        scrollLog("RO restore", {
          near: this.props.isNearBottomRef.current,
          scrollTop: scroll.scrollTop.toFixed(0),
          scrollHeight: scroll.scrollHeight,
        });
        if (MULTIPLEX_DEBUG)
          console.debug(
            `[mx-scroll] RO restore ch=${this.props.channelId?.slice(-6) ?? "-"} scrollTop-pre=${scroll.scrollTop}`,
          );
        this.props.restoreSnapshot();
      }
    });

    this.resizeObserver.observe(content);
  }

  // ─── CSS overflow-anchor verification ─────────────────────────────

  /**
   * Both the shift-up-keeps-anchor-stable behavior and the RO
   * scrollBottom-fallback path rely on the browser's CSS
   * `overflow-anchor: auto` default. If something in the scroll
   * subtree overrides it the chain silently breaks (no `skip
   * oa-compensation` events, anchor preservation stops working).
   * One-time mount warning if the computed value isn't `auto`.
   */
  private verifyOverflowAnchor() {
    const el = this.props.scrollRef.current;
    if (!el) return;
    const computed = window.getComputedStyle(el).overflowAnchor;
    if (computed !== "auto") {
      console.warn(
        `[oa] scroll container overflow-anchor='${computed}' (expected 'auto') — shift-up anchor preservation will not work as designed`,
      );
    }
  }

  // ─── Scroll handler ────────────────────────────────────────────────

  private handleScroll = () => {
    // SYNCHRONOUS — user-scrolling guard
    this.userScrollingUntil = Date.now() + 150;

    // Pointer suppression (Slack pattern)
    const content = this.contentRef.current;
    if (!this.suppressPointer && content) {
      content.style.pointerEvents = "none";
      this.suppressPointer = true;
    }
    if (this.scrollIdleTimer !== null) {
      window.clearTimeout(this.scrollIdleTimer);
    }
    this.scrollIdleTimer = window.setTimeout(() => {
      this.scrollIdleTimer = null;
      // Re-enable pointer events when scroll stops (covers keyboard/trackpad
      // scrolling where no mousemove fires)
      if (this.suppressPointer) {
        const c = this.contentRef.current;
        if (c) c.style.pointerEvents = "";
        this.suppressPointer = false;
      }
    }, 150);

    // rAF-throttled handler
    if (this.scrollRafId !== null) return;
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      if (this.channelSwitching) return;
      const el = this.props.scrollRef.current;
      if (!el) return;

      // Bail if the container is currently laid out as 0×0. Happens when an
      // ancestor has `display: none` (inactive multiplex instance) — in that
      // state scrollTop/scrollHeight clamp to 0 and scrollBottom reads as 0,
      // which would spuriously flip `isNearBottom` to true and cause a
      // pin-to-bottom on the next show transition.
      if (el.clientHeight === 0) return;

      const {
        isNearBottomRef,
        messageWindow,
        onNearBottomChange,
        scrollAnchorByChannel,
        currentAnchorIdRef,
        messageIndexById,
        channelId,
        captureSnapshot,
      } = this.props;

      // 1. isNearBottom detection — two-threshold hysteresis math.
      const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const threshold = isNearBottomRef.current
        ? STAY_AT_BOTTOM_THRESHOLD
        : RETURN_TO_BOTTOM_THRESHOLD;
      const nearScrollBottom = scrollBottom < threshold;
      let nearBottom = nearScrollBottom && !messageWindow.hasNewerInWindow;
      const wasNearBottom = isNearBottomRef.current;

      // Content-growth detection: would flip from at-bottom to
      // not-at-bottom, but scrollTop hasn't moved since the last
      // at-bottom capture AND scrollHeight actually grew. The
      // scrollHeight guard distinguishes "content grew below
      // viewport" (legit re-pin) from "user crossed the threshold
      // by 1-2 px" (false positive — would rubber-band the user
      // back to bottom on slow scroll-up). See
      // `scrollHeightWhileNearBottom` doc.
      if (
        wasNearBottom &&
        !nearBottom &&
        Math.abs(el.scrollTop - this.scrollTopWhileNearBottom) < 5 &&
        el.scrollHeight > this.scrollHeightWhileNearBottom
      ) {
        el.scrollTop = el.scrollHeight - el.clientHeight;
        this.scrollTopWhileNearBottom = el.scrollTop;
        this.scrollHeightWhileNearBottom = el.scrollHeight;
        scrollLog("scroll re-pin content-grew", {
          scrollHeight: el.scrollHeight,
          scrollBottomBefore: scrollBottom.toFixed(0),
        });
        nearBottom = true;
      }
      isNearBottomRef.current = nearBottom;

      // Keep the at-bottom baseline fresh while we're at bottom so a
      // future content-growth tick can compare against the latest
      // pinned position.
      if (nearBottom) {
        this.scrollTopWhileNearBottom = el.scrollTop;
        this.scrollHeightWhileNearBottom = el.scrollHeight;
      }

      if (nearBottom !== wasNearBottom) {
        jumpLog("scroll handler isNearBottom flip", {
          from: wasNearBottom,
          to: nearBottom,
          scrollTop: el.scrollTop,
          scrollHeight: el.scrollHeight,
          scrollBottom,
          hasNewerInWindow: messageWindow.hasNewerInWindow,
        });
        onNearBottomChange(nearBottom);
        if (nearBottom && !wasNearBottom) {
          captureSnapshot();
          messageWindow.dispatch({ type: "pin-bottom" });
        }
      }

      // 2. Capture snapshot (keeps lastSnapshotRef fresh)
      captureSnapshot();

      // 3. Continuous scrollAnchorByChannel update
      if (!nearBottom) {
        const containerTop = el.getBoundingClientRect().top;
        const msgNodes = el.querySelectorAll<HTMLElement>("[data-message-id]");

        for (let i = 0; i < msgNodes.length; i++) {
          const node = msgNodes[i];
          if (node.getBoundingClientRect().bottom > containerTop + 1) {
            const foundId = node.dataset.messageId ?? null;
            if (foundId) {
              const foundOffset =
                node.getBoundingClientRect().top - containerTop;
              let neighborId: string | null = null;
              const anchorIdx = messageIndexById.get(foundId);
              if (anchorIdx !== undefined) {
                for (let j = i + 1; j < msgNodes.length; j++) {
                  const nId = msgNodes[j].dataset.messageId;
                  if (nId) {
                    neighborId = nId;
                    break;
                  }
                }
              }

              scrollAnchorByChannel.current[channelId] = {
                id: foundId,
                neighborId,
                offset: foundOffset,
              };
              currentAnchorIdRef.current = foundId;
            }
            break;
          }
        }
      } else {
        delete scrollAnchorByChannel.current[channelId];
        currentAnchorIdRef.current = null;
      }
    });
  };

  // Re-enable pointer events after scroll stops and user moves mouse
  private handleMouseMove = () => {
    if (this.suppressPointer && this.scrollIdleTimer === null) {
      const content = this.contentRef.current;
      if (content) {
        content.style.pointerEvents = "";
      }
      this.suppressPointer = false;
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  render() {
    const {
      scrollRef,
      topSentinelRef,
      bottomSentinelRef,
      isLoadingOlder,
      hasMoreMessages,
      channelMessagesStatus,
      showMessageLoadingSkeleton,
      isUsingReconnectSnapshot,
      messageListLength,
      messageWindow,
      rows,
    } = this.props;

    const showEmpty =
      channelMessagesStatus.type === "complete" &&
      shouldShowEmptyMessageState({
        queryState: channelMessagesStatus.type,
        isLoadingSkeletonVisible: showMessageLoadingSkeleton,
        messageCount: messageListLength,
      });

    return (
      <div
        ref={scrollRef}
        onScroll={this.handleScroll}
        onMouseMove={this.handleMouseMove}
        className={cn("h-full overflow-y-auto overflow-x-hidden p-4")}
      >
        <div ref={this.contentRef} className="min-h-full flex flex-col">
          {isUsingReconnectSnapshot && (
            <div className="mb-3 rounded border border-warning/35 bg-warning-muted px-2 py-1 text-[11px] text-warning">
              Reconnecting… showing last synced messages.
            </div>
          )}

          {rows.length > 0 && !showMessageLoadingSkeleton ? (
            <>
              <div ref={topSentinelRef} style={{ height: 0 }} />

              {isLoadingOlder && (
                <div className="flex justify-center py-2">
                  <LoadingIcon size={16} className="text-muted-foreground" />
                </div>
              )}

              {!hasMoreMessages && messageWindow.windowStartIndex === 0 && (
                <div className="flex items-center justify-center py-6 text-xs text-muted-foreground/50">
                  This is the beginning of the conversation
                </div>
              )}

              {rows.map((row) => (
                <div key={row.key}>{row.node}</div>
              ))}

              <div ref={bottomSentinelRef} style={{ height: 0 }} />
            </>
          ) : (
            <>
              {showMessageLoadingSkeleton && <MessageListSkeleton />}
              {showEmpty && (
                <EmptyState
                  layout="pane"
                  icon={ChatCircle}
                  title="No messages yet"
                  description="Say hello — your message will be the first in this channel."
                />
              )}
              {channelMessagesStatus.type === "error" && (
                <div className="space-y-2 py-2">
                  <p className="text-sm text-destructive">
                    Failed to load messages.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => channelMessagesStatus.retry?.()}
                    className="px-2 py-1 text-xs"
                  >
                    Retry
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}
